import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  Row,
  Col,
  Select,
  DatePicker,
  Button,
  Space,
  Typography,
  Empty,
  Statistic,
  Tag,
  Table,
  message,
  AutoComplete,
  Avatar,
  Spin,
  Progress,
} from 'antd';
import {
  SearchOutlined,
  DownloadOutlined,
  UserOutlined,
  CalendarOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
  RiseOutlined,
  SmileOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { Doughnut } from 'react-chartjs-2';
import dayjs from 'dayjs';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import api from '../../utils/api';
import {
  fetchDailyReport,
  fetchWeeklyReport,
  fetchMonthlyReport,
} from '../../redux/slices/reportSlice';
import { fetchEmployeeList } from '../../redux/slices/employeeSlice';
import { EMOTION_COLORS, CHART_COLORS } from '../../appConst';
import { formatPercentage } from '../../utils/helpers';
import { useAuth } from '../../context/AuthContext';

const { Title, Text } = Typography;
const { Option } = Select;

const StyledCard = styled(Card)`
  border-radius: 16px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

const TableWrapper = styled.div`
  .ant-table-body {
    &::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: transparent;
      border-radius: 4px;
    }

    &:hover::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.2);
    }

    &::-webkit-scrollbar-thumb:hover {
      background: rgba(0, 0, 0, 0.3);
    }
  }
`;

const ReportHeader = styled.div`
  background: linear-gradient(135deg, #B4A0F7 0%, #B4A0F7 100%);
  padding: 32px;
  border-radius: 16px;
  margin-bottom: 24px;
  color: white;

  .report-title {
    font-size: 28px;
    font-weight: 700;
    margin: 0 0 8px 0;
  }

  .report-subtitle {
    font-size: 16px;
    opacity: 0.9;
    margin: 0;
  }

  .report-meta {
    display: flex;
    gap: 24px;
    margin-top: 16px;
    flex-wrap: wrap;

    .meta-item {
      display: flex;
      align-items: center;
      gap: 8px;
      background: rgba(255, 255, 255, 0.2);
      padding: 8px 16px;
      border-radius: 8px;

      .meta-label {
        opacity: 0.9;
        font-size: 13px;
      }

      .meta-value {
        font-weight: 600;
        font-size: 15px;
      }
    }
  }
`;

const ReportFooter = styled.div`
  margin-top: 32px;
  padding: 20px;
  background: #F7FAFC;
  border-radius: 12px;
  border-left: 4px solid #B4A0F7;

  .footer-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;

    .footer-item {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #718096;
      font-size: 13px;

      .anticon {
        color: #B4A0F7;
      }
    }
  }
`;

const SearchInputWrapper = styled.div`
  .ant-select-auto-complete {
    .ant-select-selector {
      border-radius: 12px !important;
      border: 2px solid #e8e8e8 !important;
      padding: 8px 16px !important;
      height: 48px !important;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04) !important;
      background: #ffffff;

      &:hover {
        border-color: #B4A0F7 !important;
        box-shadow: 0 4px 12px rgba(180, 160, 247, 0.15) !important;
      }

      .ant-select-selection-search-input {
        font-size: 15px;
        height: 100% !important;

        &::placeholder {
          color: #a0a0a0;
          font-weight: 400;
        }
      }

      .ant-select-selection-placeholder {
        color: #a0a0a0;
        font-size: 15px;
      }
    }

    &.ant-select-focused .ant-select-selector {
      border-color: #B4A0F7 !important;
      box-shadow: 0 4px 16px rgba(180, 160, 247, 0.25) !important;
    }
  }
`;

const Reports = () => {
  const dispatch = useDispatch();
  const reportRef = useRef(null);
  const { loading } = useSelector((state) => state.report);
  const { user, isAdmin } = useAuth();

  // For regular users, auto-set their emp_id; for admins, start empty
  const [empId, setEmpId] = useState('');
  const [reportType, setReportType] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [currentReport, setCurrentReport] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [actualCrossings, setActualCrossings] = useState(0);

  // Autocomplete state
  const [employeeSuggestions, setEmployeeSuggestions] = useState([]);
  const [searchingEmployees, setSearchingEmployees] = useState(false);

  // Debounce timer refs
  const suggestionDebounceTimer = useRef(null);
  const isSearchingRef = useRef(false);
  const lastSearchValue = useRef('');

  // Auto-load user's emp_id for regular users
  useEffect(() => {
    if (!isAdmin() && user?.emp_id) {
      setEmpId(user.emp_id);
    }
  }, [user, isAdmin]);

  // Memoize isAdmin result for stable dependency
  const isAdminUser = useMemo(() => isAdmin(), [isAdmin]);

  // Define handleGenerateReport before the useEffect that uses it
  const handleGenerateReport = useCallback(async () => {
    if (!empId) {
      message.warning('Please select an employee first');
      return;
    }

    if (!selectedDate) {
      message.warning('Please select a date');
      return;
    }

    try {
      let result;
      let startDate, endDate;

      switch (reportType) {
        case 'daily':
          startDate = selectedDate.format('YYYY-MM-DD');
          endDate = selectedDate.format('YYYY-MM-DD');
          result = await dispatch(fetchDailyReport({
            empId,
            date: startDate,
          })).unwrap();
          break;
        case 'weekly':
          startDate = selectedDate.startOf('week').format('YYYY-MM-DD');
          endDate = selectedDate.endOf('week').format('YYYY-MM-DD');
          result = await dispatch(fetchWeeklyReport({
            empId,
            weekStart: startDate,
          })).unwrap();
          break;
        case 'monthly':
          startDate = selectedDate.startOf('month').format('YYYY-MM-DD');
          endDate = selectedDate.endOf('month').format('YYYY-MM-DD');
          result = await dispatch(fetchMonthlyReport({
            empId,
            year: selectedDate.year(),
            month: selectedDate.month() + 1,
          })).unwrap();
          break;
        default:
          break;
      }

      // Fetch actual crossings
      const crossings = await fetchActualCrossings(empId, startDate, endDate);
      setActualCrossings(crossings);

      setCurrentReport(result);
      if (!result || result.total_detections === 0) {
        message.info('No data found for the selected period');
      }
    } catch (error) {
      message.error('Failed to generate report');
      console.error('Report error:', error);
    }
  }, [empId, selectedDate, reportType, dispatch]);

  // Auto-generate report when empId, reportType, or selectedDate changes (for regular users)
  useEffect(() => {
    if (!isAdminUser && empId && selectedDate) {
      handleGenerateReport();
    }
  }, [empId, reportType, selectedDate, isAdminUser, handleGenerateReport]);

  useEffect(() => {
    // Load employee list for dropdown
    dispatch(fetchEmployeeList({ limit: 1000, offset: 0 }));
  }, [dispatch]);

  const handleSearchSuggestions = useCallback((searchValue) => {
    // Clear previous timer
    if (suggestionDebounceTimer.current) {
      clearTimeout(suggestionDebounceTimer.current);
    }

    if (!searchValue || searchValue.length < 2) {
      setEmployeeSuggestions([]);
      lastSearchValue.current = '';
      return;
    }

    // Skip if already searching or same value
    if (isSearchingRef.current || lastSearchValue.current === searchValue) {
      return;
    }

    // Debounce the search to reduce API calls
    suggestionDebounceTimer.current = setTimeout(async () => {
      // Double check before making API call
      if (isSearchingRef.current || lastSearchValue.current === searchValue) {
        return;
      }

      try {
        isSearchingRef.current = true;
        lastSearchValue.current = searchValue;
        setSearchingEmployees(true);

        const result = await dispatch(fetchEmployeeList({
          search: searchValue,
          limit: 10,
          offset: 0,
        })).unwrap();

        // Filter results to only include employees that actually match the search term
        const searchLower = searchValue.toLowerCase();
        const filteredData = result.data.filter((emp) => {
          const empIdMatch = emp.emp_id?.toLowerCase().includes(searchLower);
          const nameMatch = emp.full_name?.toLowerCase().includes(searchLower);
          const emailMatch = emp.email?.toLowerCase().includes(searchLower);
          return empIdMatch || nameMatch || emailMatch;
        });

        // Limit to top 5 matches
        const topMatches = filteredData.slice(0, 5);

        const suggestions = topMatches.map((emp) => ({
          value: emp.emp_id,
          label: (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space>
                <Avatar size="small" icon={<UserOutlined />} style={{ background: '#B4A0F7' }} />
                <div>
                  <div><Text strong>{emp.emp_id}</Text> - {emp.full_name}</div>
                  {emp.email && <Text type="secondary" style={{ fontSize: 12 }}>{emp.email}</Text>}
                </div>
              </Space>
              {emp.department && <Tag color="blue">{emp.department}</Tag>}
            </div>
          ),
        }));

        setEmployeeSuggestions(suggestions);
      } catch (error) {
        console.error('Error searching employees:', error);
        setEmployeeSuggestions([]);
      } finally {
        setSearchingEmployees(false);
        isSearchingRef.current = false;
      }
    }, 400); // 400ms delay to reduce API calls while typing
  }, [dispatch]);

  const fetchActualCrossings = async (empId, startDate, endDate) => {
    try {
      // Call the API with proper parameter names
      const response = await api.getCrossingsReport(empId, startDate, endDate, null, 30, 1000);

      // The response contains a crossings array with employee summary
      // Each item has a crossing_count field showing actual crossings
      if (response && response.crossings && response.crossings.length > 0) {
        const employeeData = response.crossings.find(c => c.emp_id === empId);
        return employeeData ? employeeData.crossing_count : 0;
      }
      return 0;
    } catch (error) {
      console.error('Error fetching actual crossings:', error);
      return 0;
    }
  };

  const generateSentimentMessage = (emotionDistribution) => {
    if (!emotionDistribution || emotionDistribution.length === 0) {
      return { message: "Keep up the great work! 💪", color: '#B4A0F7' };
    }

    const topEmotion = emotionDistribution[0];
    const topPercentage = topEmotion.percentage;

    const sentiments = {
      Happy: [
        { message: "Positive sentiment detected. Employee shows high engagement and workplace satisfaction.", color: '#52c41a' },
        { message: "Elevated mood indicators observed. Maintaining optimal workplace environment.", color: '#52c41a' },
        { message: "Strong positive emotional state recorded. Performance metrics correlate favorably.", color: '#52c41a' },
      ],
      Neutral: [
        { message: "Baseline emotional state maintained. Consistent professional demeanor observed.", color: '#1890ff' },
        { message: "Stable emotional indicators. Standard operating conditions confirmed.", color: '#1890ff' },
        { message: "Professional composure maintained throughout observation period.", color: '#1890ff' },
      ],
      Sad: [
        { message: "Low mood indicators detected. Consider employee wellness check-in or support resources.", color: '#fa8c16' },
        { message: "Decreased emotional baseline observed. Monitor for potential workplace stressors.", color: '#fa8c16' },
        { message: "Subdued emotional state noted. Employee assistance programs available if needed.", color: '#fa8c16' },
      ],
      Angry: [
        { message: "Elevated stress indicators detected. Recommend conflict resolution or workload assessment.", color: '#f5222d' },
        { message: "High-intensity emotional state observed. Consider intervention or support measures.", color: '#f5222d' },
        { message: "Frustrated behavioral patterns detected. Management review recommended.", color: '#f5222d' },
      ],
    };

    const emotionMessages = sentiments[topEmotion.emotion] || sentiments.Neutral;
    const randomMessage = emotionMessages[Math.floor(Math.random() * emotionMessages.length)];

    // Add context based on percentage
    if (topPercentage > 70) {
      return { ...randomMessage, intensity: 'strong' };
    } else if (topPercentage > 50) {
      return { ...randomMessage, intensity: 'moderate' };
    } else {
      return { message: "You show great emotional balance! 🌈 Keep maintaining that equilibrium.", color: '#722ed1' };
    }
  };

  const handleDownloadPDF = async () => {
    if (!currentReport) {
      message.warning('Please generate a report first');
      return;
    }

    try {
      setDownloading(true);
      message.loading({ content: 'Generating PDF...', key: 'download' });

      const element = reportRef.current;
      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        backgroundColor: '#FFFFFF',
      });

      const imgData = canvas.toDataURL('image/png');
      // Use landscape orientation ('l') for horizontal page
      const pdf = new jsPDF('l', 'mm', 'a4');
      const pdfWidth = 297; // A4 width in landscape (mm)
      const pdfHeight = 210; // A4 height in landscape (mm)

      // Calculate dimensions to fit on one page
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);

      const imgScaledWidth = imgWidth * ratio;
      const imgScaledHeight = imgHeight * ratio;

      // Center the image on the page
      const x = (pdfWidth - imgScaledWidth) / 2;
      const y = (pdfHeight - imgScaledHeight) / 2;

      pdf.addImage(imgData, 'PNG', x, y, imgScaledWidth, imgScaledHeight);

      const reportName = `${reportType}-report-${empId}-${selectedDate ? selectedDate.format('YYYY-MM-DD') : 'report'}.pdf`;
      pdf.save(reportName);
      message.success({ content: 'PDF downloaded successfully!', key: 'download' });
    } catch (error) {
      console.error('Error generating PDF:', error);
      message.error({ content: 'Failed to generate PDF', key: 'download' });
    } finally {
      setDownloading(false);
    }
  };

  const emotionChartData = currentReport && currentReport.emotion_distribution && currentReport.emotion_distribution.length > 0
    ? {
        labels: currentReport.emotion_distribution.map((e) => e.emotion),
        datasets: [
          {
            data: currentReport.emotion_distribution.map((e) => e.count),
            backgroundColor: currentReport.emotion_distribution.map(
              (e) => EMOTION_COLORS[e.emotion] || CHART_COLORS[0]
            ),
            borderWidth: 0,
          },
        ],
      }
    : null;

  const emotionColumns = [
    {
      title: 'Emotion',
      dataIndex: 'emotion',
      key: 'emotion',
      render: (text) => (
        <Tag
          bordered
          style={{
            backgroundColor: 'transparent',
            borderColor: EMOTION_COLORS[text] || '#ccc',
            color: '#333',
            fontSize: '12px',
            padding: '2px 8px',
            borderRadius: '4px',
            fontWeight: 500
          }}
        >
          {text}
        </Tag>
      ),
    },
    {
      title: 'Occurrences',
      dataIndex: 'count',
      key: 'count',
      sorter: (a, b) => a.count - b.count,
      render: (val) => <span style={{ fontWeight: 500, color: '#374151' }}>{val}</span>,
    },
    {
      title: 'Percentage',
      dataIndex: 'percentage',
      key: 'percentage',
      render: (val) => (
        <Space>
          <Progress
            percent={parseFloat(val.toFixed(1))}
            size="small"
            strokeColor="#B4A0F7"
            style={{ width: 80 }}
          />
          <Text>{formatPercentage(val)}</Text>
        </Space>
      ),
      sorter: (a, b) => a.percentage - b.percentage,
    },
    {
      title: 'Confidence Score',
      dataIndex: 'avg_score',
      key: 'avg_score',
      render: (val) => (
        <Tag color={val > 0.7 ? 'green' : val > 0.5 ? 'orange' : 'red'}>
          {val ? val.toFixed(3) : '-'}
        </Tag>
      ),
      sorter: (a, b) => (a.avg_score || 0) - (b.avg_score || 0),
    },
  ];

  return (
    <div>
      {currentReport && currentReport.total_detections > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 12 }}>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownloadPDF}
            loading={downloading}
            size="large"
            style={{ background: '#B4A0F7', borderColor: '#B4A0F7' }}
          >
            Download Report
          </Button>
        </div>
      )}

      {/* Filters */}
      <StyledCard style={{ marginBottom: 12 }}>
        <Row gutter={[16, 16]} align="middle">
          {/* Only show employee search for admins */}
          {isAdmin() && (
            <Col span={8}>
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Text strong style={{ color: '#595959', fontSize: '13px' }}>Employee ID or Name</Text>
                <SearchInputWrapper>
                  <AutoComplete
                    value={empId}
                    options={employeeSuggestions}
                    onSearch={handleSearchSuggestions}
                    onSelect={(value) => setEmpId(value)}
                    onChange={(value) => setEmpId(value)}
                    placeholder="Search employee for report..."
                    style={{ width: '100%' }}
                    size="large"
                    notFoundContent={searchingEmployees ? <Spin size="small" /> : null}
                    popupMatchSelectWidth={400}
                    allowClear
                  />
                </SearchInputWrapper>
              </Space>
            </Col>
          )}

          <Col span={6}>
            <Space direction="vertical" size={0} style={{ width: '100%' }}>
              <Text>Report Type</Text>
              <Select
                size="large"
                value={reportType}
                onChange={setReportType}
                style={{ width: '100%' }}
              >
                <Option value="daily">Daily Report</Option>
                <Option value="weekly">Weekly Report</Option>
                <Option value="monthly">Monthly Report</Option>
              </Select>
            </Space>
          </Col>

          <Col span={6}>
            <Space direction="vertical" size={0} style={{ width: '100%' }}>
              <Text>Date</Text>
              <DatePicker
                size="large"
                value={selectedDate}
                onChange={setSelectedDate}
                style={{ width: '100%' }}
                picker={reportType === 'monthly' ? 'month' : 'date'}
              />
            </Space>
          </Col>

          {/* Only show Generate button for admins */}
          {isAdmin() && (
            <Col span={6}>
              <Button
                type="primary"
                size="large"
                icon={<SearchOutlined />}
                onClick={handleGenerateReport}
                loading={loading}
                style={{ width: '100%', marginTop: 22 }}
              >
                Generate Report
              </Button>
            </Col>
          )}
        </Row>
      </StyledCard>

      {/* Report Results */}
      {currentReport && currentReport.total_detections > 0 ? (
        <div ref={reportRef}>
          {/* Professional Header */}
          <ReportHeader>
            <div className="report-title">
              <FileTextOutlined style={{ marginRight: 12 }} />
              Employee Emotion Analytics Report
            </div>
            <div className="report-subtitle">
              {reportType.charAt(0).toUpperCase() + reportType.slice(1)} Analysis • {currentReport.full_name || empId}
            </div>
            <div className="report-meta">
              <div className="meta-item">
                <UserOutlined />
                <div>
                  <div className="meta-label">Employee</div>
                  <div className="meta-value">{currentReport.full_name || empId}</div>
                </div>
              </div>
              <div className="meta-item">
                <CalendarOutlined />
                <div>
                  <div className="meta-label">Report Period</div>
                  <div className="meta-value">
                    {selectedDate && reportType === 'daily' && selectedDate.format('MMM DD, YYYY')}
                    {selectedDate && reportType === 'weekly' && `Week of ${selectedDate.startOf('week').format('MMM DD, YYYY')}`}
                    {selectedDate && reportType === 'monthly' && selectedDate.format('MMMM YYYY')}
                  </div>
                </div>
              </div>
              <div className="meta-item">
                <BarChartOutlined />
                <div>
                  <div className="meta-label">Total Detections</div>
                  <div className="meta-value">{currentReport.total_detections}</div>
                </div>
              </div>
              <div className="meta-item">
                <ClockCircleOutlined />
                <div>
                  <div className="meta-label">Generated</div>
                  <div className="meta-value">{dayjs().format('MMM DD, YYYY hh:mm A')}</div>
                </div>
              </div>
            </div>
          </ReportHeader>

          {/* Personalized Sentiment Message */}
          {(() => {
            const sentiment = generateSentimentMessage(currentReport.emotion_distribution);
            return (
              <Card
                style={{
                  marginBottom: 12,
                  background: `linear-gradient(135deg, ${sentiment.color}15 0%, ${sentiment.color}05 100%)`,
                  border: `2px solid ${sentiment.color}30`,
                  borderRadius: '16px',
                  padding: '20px',
                }}
              >
                <div style={{ textAlign: 'center' }}>
                  <Title level={4} style={{ margin: 0, marginBottom: 12, color: sentiment.color }}>
                    Personalized Message
                  </Title>
                  <Text style={{ fontSize: 16, color: '#2D3748' }}>
                    {sentiment.message}
                  </Text>
                </div>
              </Card>
            );
          })()}

          {/* Key Insights */}
          <Row gutter={[16, 16]} style={{ marginBottom: 12 }}>
            <Col span={6}>
              <StyledCard>
                <Statistic
                  title="Area Crossings"
                  value={actualCrossings}
                  valueStyle={{ color: '#52c41a' }}
                  suffix="times"
                  prefix={<RiseOutlined />}
                />
                <Text type="secondary" style={{ fontSize: 11 }}>Physical crossings (30s threshold)</Text>
              </StyledCard>
            </Col>
            <Col span={6}>
              <StyledCard>
                <Statistic
                  title="Track Sessions"
                  value={currentReport.crossing_count || 0}
                  valueStyle={{ color: '#667eea' }}
                  suffix="sessions"
                />
                <Text type="secondary" style={{ fontSize: 11 }}>Camera tracking sessions</Text>
              </StyledCard>
            </Col>
            <Col span={6}>
              <StyledCard>
                <Statistic
                  title="Dominant Emotion"
                  value={
                    (currentReport.emotion_distribution && currentReport.emotion_distribution[0]?.emotion) || 'N/A'
                  }
                  valueStyle={{ fontSize: 18, color: '#FFD3B6' }}
                  prefix={<SmileOutlined />}
                />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {currentReport.emotion_distribution && currentReport.emotion_distribution[0]?.percentage
                    ? `${currentReport.emotion_distribution[0].percentage.toFixed(1)}% of time`
                    : 'Most frequent emotion'}
                </Text>
              </StyledCard>
            </Col>
            <Col span={6}>
              <StyledCard>
                <Statistic
                  title="Confidence Score"
                  value={
                    currentReport.emotion_distribution && currentReport.emotion_distribution.length > 0
                      ? (currentReport.emotion_distribution.reduce((sum, e) => sum + (e.avg_score || 0), 0) / currentReport.emotion_distribution.length).toFixed(3)
                      : '0.000'
                  }
                  valueStyle={{ fontSize: 18, color: '#B4A0F7' }}
                />
                <Text type="secondary" style={{ fontSize: 11 }}>Average detection score</Text>
              </StyledCard>
            </Col>
          </Row>

          {/* Charts */}
          <Row gutter={[16, 16]} style={{ marginBottom: 12 }}>
            <Col span={12}>
              <StyledCard
                title={<Title level={4}>Emotion Distribution</Title>}
              >
                <div style={{ height: 350 }}>
                  {emotionChartData && (
                    <Doughnut
                      data={emotionChartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: 'bottom',
                          },
                        },
                      }}
                    />
                  )}
                </div>
              </StyledCard>
            </Col>

            <Col span={12}>
              <StyledCard title={<Title level={4}>Detailed Breakdown</Title>}>
                <TableWrapper>
                  <Table
                    columns={emotionColumns}
                    dataSource={currentReport.emotion_distribution}
                    rowKey="emotion"
                    pagination={false}
                    size="small"
                    scroll={{ y: 'calc(100vh - 600px)' }}
                  />
                </TableWrapper>
              </StyledCard>
            </Col>
          </Row>

          {/* Analytics Summary */}
          <StyledCard
            title={<Title level={4}>Analytics Summary</Title>}
            style={{ marginBottom: 12, background: 'linear-gradient(135deg, rgba(157, 124, 237, 0.05) 0%, rgba(180, 160, 247, 0.05) 100%)' }}
          >
            <Row gutter={[24, 24]}>
              <Col span={12}>
                <div style={{ padding: '16px', background: 'white', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                  <Title level={5} style={{ color: '#B4A0F7', marginBottom: 12 }}>
                    Emotional State Analysis
                  </Title>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <Text strong>Primary Emotion: </Text>
                      <Tag color="purple" style={{ fontSize: '14px', padding: '4px 12px' }}>
                        {(currentReport.emotion_distribution && currentReport.emotion_distribution[0]?.emotion) || 'N/A'}
                      </Tag>
                      <Text type="secondary">
                        ({(currentReport.emotion_distribution && currentReport.emotion_distribution[0]?.percentage.toFixed(1)) || '0'}% of time)
                      </Text>
                    </div>
                    <div>
                      <Text strong>Emotional Diversity: </Text>
                      <Text>{currentReport.emotion_distribution?.length || 0} different emotions detected</Text>
                    </div>
                    <div>
                      <Text strong>Detection Quality: </Text>
                      <Tag color={
                        currentReport.emotion_distribution && currentReport.emotion_distribution.length > 0 &&
                        (currentReport.emotion_distribution.reduce((sum, e) => sum + (e.avg_score || 0), 0) / currentReport.emotion_distribution.length) > 0.7
                          ? 'green'
                          : 'orange'
                      }>
                        {currentReport.emotion_distribution && currentReport.emotion_distribution.length > 0
                          ? (currentReport.emotion_distribution.reduce((sum, e) => sum + (e.avg_score || 0), 0) / currentReport.emotion_distribution.length) > 0.7
                            ? 'High Confidence'
                            : 'Moderate Confidence'
                          : 'N/A'}
                      </Tag>
                    </div>
                  </Space>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ padding: '16px', background: 'white', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                  <Title level={5} style={{ color: '#B4A0F7', marginBottom: 12 }}>
                    Activity Metrics
                  </Title>
                  <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <div>
                      <Text strong>Physical Area Crossings: </Text>
                      <Text style={{ fontSize: '16px', color: '#52c41a' }}>{actualCrossings}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 11 }}>Actual crossings with 30-second threshold (from Crossings Report)</Text>
                    </div>
                    <div>
                      <Text strong>Total Face Detections: </Text>
                      <Text style={{ fontSize: '16px', color: '#A8E6CF' }}>{currentReport.total_detections}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 11 }}>Total times face was detected across all sessions</Text>
                    </div>
                    <div>
                      <Text strong>Camera Tracking Sessions: </Text>
                      <Text style={{ fontSize: '16px', color: '#667eea' }}>{currentReport.crossing_count || 0}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 11 }}>Unique camera tracking sessions (distinct track_id)</Text>
                    </div>
                    <div>
                      <Text strong>Avg Detections per Crossing: </Text>
                      <Text style={{ fontSize: '16px', color: '#FFD3B6' }}>
                        {actualCrossings > 0
                          ? (currentReport.total_detections / actualCrossings).toFixed(1)
                          : '0'}
                      </Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 11 }}>Average detections per physical crossing</Text>
                    </div>
                  </Space>
                </div>
              </Col>
            </Row>
          </StyledCard>

          {/* Daily/Weekly Breakdown */}
          {(currentReport.daily_breakdown || currentReport.weekly_breakdown) && (
            <StyledCard
              title={
                <Title level={4}>
                  {reportType === 'weekly' ? 'Daily' : reportType === 'monthly' ? 'Weekly' : ''} Breakdown
                </Title>
              }
            >
              <Row gutter={[16, 16]}>
                {Object.entries(
                  currentReport.daily_breakdown || currentReport.weekly_breakdown || {}
                ).map(([date, emotions]) => (
                  <Col span={6} key={date}>
                    <Card size="small" title={dayjs(date).format('MMM DD, YYYY')}>
                      {emotions && emotions.length > 0 ? (
                        emotions.map((e, idx) => (
                          <div
                            key={idx}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              marginBottom: 8,
                            }}
                          >
                            <Tag style={{ backgroundColor: EMOTION_COLORS[e.emotion] || '#ccc', border: 'none' }}>
                              {e.emotion}
                            </Tag>
                            <Text strong>{e.count}</Text>
                          </div>
                        ))
                      ) : (
                        <Text type="secondary">No data</Text>
                      )}
                    </Card>
                  </Col>
                ))}
              </Row>
            </StyledCard>
          )}

          {/* Professional Footer */}
          <ReportFooter>
            <div className="footer-content">
              <div className="footer-item">
                <ClockCircleOutlined />
                <span>Report Generated: {dayjs().format('MMMM DD, YYYY [at] hh:mm:ss A')}</span>
              </div>
              <div className="footer-item">
                <FileTextOutlined />
                <span>Report Type: {reportType.charAt(0).toUpperCase() + reportType.slice(1)}</span>
              </div>
              <div className="footer-item">
                <BarChartOutlined />
                <span>Avaniko Face Recognition System • Confidential</span>
              </div>
            </div>
          </ReportFooter>
        </div>
      ) : currentReport && currentReport.total_detections === 0 ? (
        <StyledCard>
          <Empty
            description="No emotion data found for this employee in the selected period"
            style={{ padding: '60px 0' }}
          />
        </StyledCard>
      ) : (
        <StyledCard>
          <Empty
            description="Select employee and generate report to view analytics"
            style={{ padding: '60px 0' }}
          />
        </StyledCard>
      )}
    </div>
  );
};

export default Reports;
